import { useEffect, useRef, useState, useCallback } from 'react'
import GameRanking from '../components/GameRanking'
import { saveScore, loadGlobalRanking, loadPersonalRanking } from '../utils/firestoreService'
import UserHeader from '../components/UserHeader'
import GameStats from '../components/GameStats'
import RankingModal from '../components/RankingModal'
import PaymentModal from '../components/PaymentModal'
import './SanCode.css'

const CANVAS_WIDTH = 800
const CANVAS_HEIGHT = 600
const GRAVITY = 0.5
const JUMP_FORCE = -11
const MOVE_SPEED = 4
const FPS = 60
const FRAME_TIME = 1000 / FPS

export default function SanCode({ user, onBackToMenu, userProgress }) {
  const canvasRef = useRef()
  const [score, setScore] = useState(0)
  const [lives, setLives] = useState(3)
  const [level, setLevel] = useState(1)
  const [gameOver, setGameOver] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [gameStarted, setGameStarted] = useState(false)
  const [globalRanking, setGlobalRanking] = useState([])
  const [personalRanking, setPersonalRanking] = useState([])
  const [showRanking, setShowRanking] = useState(false)
  const [savingScore, setSavingScore] = useState(false)
  const [scoreSaved, setScoreSaved] = useState(false)
  const [powerUp, setPowerUp] = useState(null) // 'cpu' or 'ram'
  const [powerUpTime, setPowerUpTime] = useState(0)
  const [trialBlocked, setTrialBlocked] = useState(false)
  const [levelComplete, setLevelComplete] = useState(false)
  const [invincible, setInvincible] = useState(false)
  const [invincibleTime, setInvincibleTime] = useState(0)
  const [showPayment, setShowPayment] = useState(false)
  
  // Estados dos controles mobile
  const [touchControls, setTouchControls] = useState({
    left: false,
    right: false,
    jump: false
  })
  const joystickRef = useRef(null)
  const jumpButtonRef = useRef(null)

  const gameStateRef = useRef({
    player: {
      x: 150,
      y: 400,
      width: 40,
      height: 48,
      velocityY: 0,
      velocityX: 0,
      onGround: false,
      direction: 'right',
      isRunning: false,
      animFrame: 0
    },
    platforms: [],
    enemies: [],
    items: [],
    keys: {},
    touchControls: { left: false, right: false, jump: false },
    coins: 0,
    boss: null,
    isBossLevel: false,
    lastFrameTime: 0
  })

  const saveGameScore = useCallback(async (finalScore) => {
    if (!user || finalScore === 0) return
    
    try {
      setSavingScore(true)
      await saveScore({
        userId: user.uid,
        userName: user.displayName || user.email,
        userPhoto: user.photoURL,
        game: 'sancode',
        score: finalScore,
        level: level,
        lines: Math.floor(finalScore / 100)
      })
      setScoreSaved(true)
      setTimeout(() => setScoreSaved(false), 3000)
    } catch (error) {
      console.error('Erro ao salvar score:', error)
    } finally {
      setSavingScore(false)
    }
  }, [user, level])

  const loadRankings = useCallback(async () => {
    try {
      const [global, personal] = await Promise.all([
        loadGlobalRanking('sancode'),
        loadPersonalRanking(user?.uid, 'sancode')
      ])
      setGlobalRanking(global)
      setPersonalRanking(personal)
    } catch (error) {
      console.error('Error loading rankings:', error)
    }
  }, [user])

  useEffect(() => {
    loadRankings()
  }, [loadRankings])

  // Verifica se usuário tem acesso ao jogo completo
  useEffect(() => {
    const checkAccess = async () => {
      if (level > 3 && user) {
        try {
          const response = await fetch(
            `https://us-central1-tetriz-sandevcode.cloudfunctions.net/api/check-game-access/${user.uid}/sancode`
          )
          const data = await response.json()
          
          if (!data.hasAccess) {
            setTrialBlocked(true)
            setIsPaused(true)
          }
        } catch (error) {
          console.error('Erro ao verificar acesso:', error)
        }
      }
    }
    
    checkAccess()
  }, [level, user])

  // Desenha personagem mais humano
  const drawPlayer = useCallback((ctx, player, powerUp) => {
    const x = player.x
    const y = player.y
    const w = player.width
    const h = player.height
    
    // Cor baseada em power-up
    const bodyColor = powerUp === 'cpu' ? '#FFD700' : (powerUp === 'ram' ? '#00FF00' : '#0099FF')
    
    // Corpo
    ctx.fillStyle = bodyColor
    ctx.fillRect(x + 8, y + 12, w - 16, h - 20) // Torso
    
    // Cabeça
    ctx.fillStyle = '#FFD4A3' // Pele
    ctx.beginPath()
    ctx.arc(x + w/2, y + 8, 8, 0, Math.PI * 2)
    ctx.fill()
    
    // Cabelo
    ctx.fillStyle = '#4A2C0F'
    ctx.beginPath()
    ctx.arc(x + w/2, y + 5, 8, Math.PI, Math.PI * 2)
    ctx.fill()
    
    // Olhos
    ctx.fillStyle = '#FFF'
    ctx.fillRect(x + 14, y + 7, 4, 4)
    ctx.fillRect(x + 22, y + 7, 4, 4)
    
    // Pupilas
    ctx.fillStyle = '#000'
    const pupilOffset = player.direction === 'right' ? 2 : 1
    ctx.fillRect(x + 15 + pupilOffset, y + 8, 2, 2)
    ctx.fillRect(x + 23 + pupilOffset, y + 8, 2, 2)
    
    // Sorriso
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.arc(x + w/2, y + 10, 4, 0, Math.PI)
    ctx.stroke()
    
    // Braços
    ctx.fillStyle = '#FFD4A3'
    if (player.isRunning) {
      // Braços em movimento
      const armSwing = Math.sin(player.animFrame * 0.3) * 4
      ctx.fillRect(x + 4, y + 16 + armSwing, 6, 12)
      ctx.fillRect(x + 30, y + 16 - armSwing, 6, 12)
    } else {
      ctx.fillRect(x + 4, y + 16, 6, 12)
      ctx.fillRect(x + 30, y + 16, 6, 12)
    }
    
    // Pernas
    ctx.fillStyle = '#2C5AA0'
    if (player.isRunning && player.onGround) {
      // Pernas em movimento
      const legSwing = Math.sin(player.animFrame * 0.3) * 3
      ctx.fillRect(x + 12, y + h - 16 + legSwing, 6, 16)
      ctx.fillRect(x + 22, y + h - 16 - legSwing, 6, 16)
    } else {
      ctx.fillRect(x + 12, y + h - 16, 6, 16)
      ctx.fillRect(x + 22, y + h - 16, 6, 16)
    }
    
    // Símbolo no peito
    ctx.fillStyle = '#FFF'
    ctx.font = 'bold 10px monospace'
    ctx.fillText('{ }', x + 12, y + 26)
  }, [])

  // Desenha inimigo mais humano
  const drawEnemy = useCallback((ctx, enemy, cameraX) => {
    const x = enemy.x - cameraX
    const y = enemy.y
    const w = enemy.width
    const h = enemy.height
    
    if (x < -100 || x > CANVAS_WIDTH + 100) return
    
    const color = enemy.type === 'bug' ? '#FF4500' : '#8B008B'
    
    // Corpo do inimigo
    ctx.fillStyle = color
    ctx.fillRect(x + 5, y + 10, w - 10, h - 18)
    
    // Cabeça
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.arc(x + w/2, y + 8, 7, 0, Math.PI * 2)
    ctx.fill()
    
    // Olhos malvados
    ctx.fillStyle = '#FF0000'
    ctx.fillRect(x + 8, y + 6, 5, 5)
    ctx.fillRect(x + 17, y + 6, 5, 5)
    
    // Pupilas
    ctx.fillStyle = '#000'
    ctx.fillRect(x + 10, y + 7, 2, 3)
    ctx.fillRect(x + 19, y + 7, 2, 3)
    
    // Dentes
    ctx.fillStyle = '#FFF'
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(x + 8 + i * 5, y + 14, 3, 4)
    }
    
    // Braços
    ctx.fillStyle = color
    ctx.fillRect(x + 2, y + 14, 5, 10)
    ctx.fillRect(x + w - 7, y + 14, 5, 10)
    
    // Pernas
    ctx.fillRect(x + 8, y + h - 12, 5, 12)
    ctx.fillRect(x + 17, y + h - 12, 5, 12)
  }, [])

  // Gera plataformas randomizadas
  const generateRandomPlatforms = useCallback((levelNum) => {
    const platforms = [
      // Chão sempre presente
      { x: 0, y: 550, width: 800, height: 50, type: 'ground' }
    ]

    const platformCount = 5 + Math.floor(levelNum / 2)
    const usedPositions = []

    for (let i = 0; i < platformCount; i++) {
      let x, y, width
      let attempts = 0
      let validPosition = false

      while (!validPosition && attempts < 50) {
        width = 80 + Math.random() * 120
        x = Math.random() * (CANVAS_WIDTH - width)
        y = 350 + Math.random() * 150  // Altura alcançável (entre 350-500)

        // eslint-disable-next-line no-loop-func
        validPosition = !usedPositions.some(pos => 
          Math.abs(pos.x - x) < 150 && Math.abs(pos.y - y) < 80
        )
        attempts++
      }

      if (validPosition) {
        platforms.push({
          x: Math.floor(x),
          y: Math.floor(y),
          width: Math.floor(width),
          height: 20,
          type: 'platform'
        })
        usedPositions.push({ x, y })
      }
    }

    return platforms
  }, [])

  // Gera inimigos randomizados
  const generateRandomEnemies = useCallback((levelNum, platforms) => {
    const enemies = []
    const isBossLevel = levelNum % 10 === 0
    
    if (isBossLevel) {
      return enemies // Sem inimigos normais no nível do boss
    }

    const enemyCount = 2 + Math.floor(levelNum / 3)
    
    for (let i = 0; i < enemyCount && i < 8; i++) {
      const platform = platforms[Math.floor(Math.random() * platforms.length)]
      const type = Math.random() > 0.5 ? 'bug' : 'virus'
      const speed = type === 'bug' ? 1.5 + Math.random() * 1 : 1 + Math.random() * 0.8
      
      enemies.push({
        x: platform.x + Math.random() * (platform.width - 30),
        y: platform.y - 30,
        width: 30,
        height: 30,
        speed: speed,
        direction: Math.random() > 0.5 ? 1 : -1,
        type: type,
        velocityY: 0
      })
    }

    return enemies
  }, [])

  // Gera itens randomizados
  const generateRandomItems = useCallback((levelNum, platforms) => {
    const items = []
    const itemCount = 5 + Math.floor(levelNum / 2)

    for (let i = 0; i < itemCount && i < 12; i++) {
      const platform = platforms[Math.floor(Math.random() * platforms.length)]
      let type
      
      const rand = Math.random()
      // 70% moedas, 15% CPU, 15% RAM (power-ups mais raros)
      if (rand < 0.70) {
        type = 'coin'
      } else if (rand < 0.85) {
        type = 'cpu'
      } else {
        type = 'ram'
      }

      items.push({
        x: platform.x + Math.random() * (platform.width - 25),
        y: platform.y - 35,
        width: 25,
        height: 25,
        type: type,
        collected: false
      })
    }

    return items
  }, [])

  // Cria boss para níveis múltiplos de 10
  const createBoss = useCallback((levelNum) => {
    const bossHealth = 3 + Math.floor(levelNum / 10)
    
    return {
      x: CANVAS_WIDTH / 2 - 40,
      y: 100,
      width: 80,
      height: 80,
      velocityY: 0,
      health: bossHealth,
      maxHealth: bossHealth,
      speed: 2,
      direction: 1,
      attackCooldown: 0,
      phase: 1,
      invulnerable: false
    }
  }, [])

  // Gera nova plataforma
  const generatePlatform = useCallback((startX) => {
    const width = 100 + Math.random() * 150
    const y = 450 + Math.random() * 100
    
    return {
      x: startX,
      y: y,
      width: width,
      height: 20,
      type: 'platform'
    }
  }, [])

  // Gera novo inimigo
  const generateEnemy = useCallback((startX) => {
    const type = Math.random() > 0.5 ? 'bug' : 'virus'
    
    return {
      x: startX,
      y: 500,
      width: 30,
      height: 36,
      speed: type === 'bug' ? 1.5 : 1,
      direction: -1,
      type: type
    }
  }, [])

  // Gera novo item
  const generateItem = useCallback((startX) => {
    const rand = Math.random()
    let type
    
    if (rand < 0.7) {
      type = 'coin'
    } else if (rand < 0.85) {
      type = 'cpu'
    } else {
      type = 'ram'
    }
    
    return {
      x: startX,
      y: 400 + Math.random() * 100,
      width: 25,
      height: 25,
      type: type,
      collected: false
    }
  }, [])

  // Inicializa o nível
  const initLevel = useCallback(() => {
    console.log('🔄 INIT LEVEL chamado para nível:', level)
    const state = gameStateRef.current
    const isBossLevel = level % 10 === 0
    
    // Reset player
    state.player.x = 100
    state.player.y = 400
    state.player.velocityY = 0
    state.player.velocityX = 0
    state.player.onGround = false
    
    // Gera plataformas
    const platforms = generateRandomPlatforms(level)
    const enemies = generateRandomEnemies(level, platforms)
    const items = generateRandomItems(level, platforms)
    
    state.platforms = platforms
    state.enemies = enemies
    state.items = items
    state.isBossLevel = isBossLevel
    
    if (isBossLevel) {
      state.boss = createBoss(level)
    } else {
      state.boss = null
    }
  }, [level, generateRandomPlatforms, generateRandomEnemies, generateRandomItems, createBoss])

  // Inicializa o nível apenas quando o level muda
  useEffect(() => {
    console.log('📍 Level mudou para:', level)
    initLevel()
  }, [level, initLevel])

  // Game Loop
  useEffect(() => {
    if (gameOver || isPaused) return

    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')

    let animationFrameId

    const gameLoop = (timestamp) => {
      if (gameOver || isPaused || trialBlocked) return

      const state = gameStateRef.current
      
      // Controle de FPS
      if (!state.lastFrameTime) state.lastFrameTime = timestamp
      const deltaTime = timestamp - state.lastFrameTime
      
      if (deltaTime < FRAME_TIME) {
        animationFrameId = requestAnimationFrame(gameLoop)
        return
      }
      
      state.lastFrameTime = timestamp - (deltaTime % FRAME_TIME)
      const player = state.player

      // Clear canvas
      ctx.fillStyle = '#87CEEB' // Céu azul
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

      // Update player physics
      const currentSpeed = MOVE_SPEED * (powerUp === 'ram' ? 1.3 : 1)
      
      // Controles de teclado ou touch
      const movingLeft = state.keys['ArrowLeft'] || state.keys['a'] || state.touchControls.left
      const movingRight = state.keys['ArrowRight'] || state.keys['d'] || state.touchControls.right
      const jumping = state.keys['ArrowUp'] || state.keys['w'] || state.keys[' '] || state.touchControls.jump
      
      if (movingLeft) {
        player.velocityX = -currentSpeed
        player.direction = 'left'
      } else if (movingRight) {
        player.velocityX = currentSpeed
        player.direction = 'right'
      } else {
        player.velocityX *= 0.8 // Suaviza a parada
      }

      if (jumping && player.onGround) {
        player.velocityY = JUMP_FORCE
        player.onGround = false
        // Reset jump para não pular continuamente
        if (touchControls.jump) {
          setTouchControls(prev => ({ ...prev, jump: false }))
        }
      }

      // Apply gravity
      player.velocityY += GRAVITY
      if (player.velocityY > 15) player.velocityY = 15 // Terminal velocity
      
      player.x += player.velocityX
      player.y += player.velocityY

      // Check platform collisions
      player.onGround = false
      if (state.platforms && Array.isArray(state.platforms)) {
        state.platforms.forEach(platform => {
          if (
            player.x < platform.x + platform.width &&
            player.x + player.width > platform.x &&
            player.y + player.height > platform.y &&
            player.y + player.height < platform.y + platform.height &&
            player.velocityY > 0
          ) {
            player.y = platform.y - player.height
            player.velocityY = 0
            player.onGround = true
          }
        })
      }

      // Boundaries
      if (player.x < 0) player.x = 0
      if (player.x + player.width > CANVAS_WIDTH) player.x = CANVAS_WIDTH - player.width
      if (player.y > CANVAS_HEIGHT) {
        setLives(prev => {
          const newLives = prev - 1
          if (newLives <= 0) {
            setGameOver(true)
            saveGameScore(score)
          } else {
            console.log('CAIR DO MAPA - Reiniciando nível')
            initLevel()
          }
          return newLives
        })
      }

      // Draw platforms
      if (state.platforms && Array.isArray(state.platforms)) {
        state.platforms.forEach(platform => {
          if (platform.type === 'ground') {
            ctx.fillStyle = '#228B22'
            ctx.fillRect(platform.x, platform.y, platform.width, platform.height)
            ctx.fillStyle = '#1a6b1a'
            for (let i = 0; i < platform.width; i += 20) {
              ctx.fillRect(platform.x + i, platform.y, 2, platform.height)
            }
          } else {
            ctx.fillStyle = '#8B4513'
            ctx.fillRect(platform.x, platform.y, platform.width, platform.height)
            ctx.strokeStyle = '#654321'
            ctx.lineWidth = 2
            ctx.strokeRect(platform.x, platform.y, platform.width, platform.height)
          }
        })
      }

      // Update and draw boss
      if (state.boss) {
        const boss = state.boss
        
        // Boss physics
        boss.velocityY += GRAVITY
        boss.y += boss.velocityY
        boss.x += boss.speed * boss.direction

        // Boss platform collision
        if (state.platforms && Array.isArray(state.platforms)) {
          state.platforms.forEach(platform => {
            if (
              boss.x < platform.x + platform.width &&
            boss.x + boss.width > platform.x &&
            boss.y + boss.height > platform.y &&
            boss.y + boss.height < platform.y + platform.height &&
            boss.velocityY > 0
          ) {
            boss.y = platform.y - boss.height
            boss.velocityY = 0
          }
        })
        }

        // Boss bounce
        if (boss.x < 0 || boss.x + boss.width > CANVAS_WIDTH) {
          boss.direction *= -1
        }

        // Boss attacks
        boss.attackCooldown--
        if (boss.attackCooldown <= 0 && Math.random() < 0.02) {
          boss.velocityY = JUMP_FORCE * 0.8
          boss.attackCooldown = 120
        }

        // Draw boss (Super Vírus)
        ctx.save()
        ctx.fillStyle = boss.invulnerable ? '#FFD700' : '#8B008B'
        ctx.fillRect(boss.x, boss.y, boss.width, boss.height)
        
        // Boss eyes
        ctx.fillStyle = '#FF0000'
        ctx.fillRect(boss.x + 15, boss.y + 20, 15, 15)
        ctx.fillRect(boss.x + 50, boss.y + 20, 15, 15)
        
        // Boss teeth
        ctx.fillStyle = '#FFF'
        for (let i = 0; i < 5; i++) {
          ctx.fillRect(boss.x + 10 + i * 14, boss.y + 60, 10, 15)
        }

        // Boss health bar
        ctx.fillStyle = '#000'
        ctx.fillRect(boss.x, boss.y - 15, boss.width, 10)
        ctx.fillStyle = '#FF0000'
        ctx.fillRect(boss.x + 2, boss.y - 13, (boss.width - 4) * (boss.health / boss.maxHealth), 6)
        
        ctx.restore()

        // Boss collision with player
        if (
          player.x < boss.x + boss.width &&
          player.x + player.width > boss.x &&
          player.y < boss.y + boss.height &&
          player.y + player.height > boss.y
        ) {
          if (!boss.invulnerable && player.velocityY > 0 && player.y < boss.y + 20) {
            // Acertou o boss
            boss.health--
            boss.invulnerable = true
            player.velocityY = JUMP_FORCE * 0.7
            setScore(prev => prev + 100)
            
            setTimeout(() => {
              if (state.boss) state.boss.invulnerable = false
            }, 500)

            if (boss.health <= 0) {
              // Boss derrotado
              console.log('BOSS DERROTADO - Avançando nível')
              setScore(prev => prev + 500)
              setLevel(prev => prev + 1)
              state.boss = null
              setTimeout(() => initLevel(), 1000)
            }
          } else if (!boss.invulnerable && !invincible) {
            // Perdeu vida ao tocar no boss (apenas se não estiver invencível)
            setInvincible(true)
            setInvincibleTime(120) // 2 segundos de invencibilidade
            setLives(prev => {
              const newLives = prev - 1
              if (newLives <= 0) {
                setGameOver(true)
                saveGameScore(score)
              } else {
                // Reposiciona jogador sem reiniciar nível
                player.x = 100
                player.y = 400
                player.velocityY = 0
                player.velocityX = 0
              }
              return newLives
            })
          }
        }
      }

      // Update and draw enemies
      if (state.enemies && Array.isArray(state.enemies)) {
        const enemiesToRemove = []
        
        state.enemies.forEach((enemy, idx) => {
          // Adiciona velocidade vertical se não existir
          if (enemy.velocityY === undefined) {
            enemy.velocityY = 0
          }
          
          // Aplica gravidade
          enemy.velocityY += GRAVITY
          if (enemy.velocityY > 15) enemy.velocityY = 15
          
          enemy.y += enemy.velocityY
          enemy.x += enemy.speed * enemy.direction
          
          // Colisão com plataformas
          let onPlatform = false
          if (state.platforms && Array.isArray(state.platforms)) {
            state.platforms.forEach(platform => {
              if (
                enemy.x < platform.x + platform.width &&
                enemy.x + enemy.width > platform.x &&
                enemy.y + enemy.height > platform.y &&
                enemy.y + enemy.height < platform.y + platform.height &&
                enemy.velocityY > 0
              ) {
                enemy.y = platform.y - enemy.height
                enemy.velocityY = 0
                onPlatform = true
              }
            })
          }
          
          // Vira ao chegar na borda da plataforma ou parede
          if (enemy.x < 0 || enemy.x + enemy.width > CANVAS_WIDTH) {
            enemy.direction *= -1
          }
          
          // Marca inimigo que cai para remoção
          if (enemy.y > CANVAS_HEIGHT) {
            enemiesToRemove.push(idx)
            return
        }

        // Draw enemy - PERFIL
        const enemyColor = enemy.type === 'bug' ? '#FF4500' : '#8B008B'
        const enemyFacingRight = enemy.direction === 1
        
        // Corpo
        ctx.fillStyle = enemyColor
        ctx.fillRect(enemy.x + 5, enemy.y + 8, enemy.width - 10, enemy.height - 12)
        
        // Cabeça
        ctx.beginPath()
        ctx.arc(enemy.x + enemy.width/2, enemy.y + 6, 6, 0, Math.PI * 2)
        ctx.fill()
        
        // Chifre
        ctx.fillStyle = '#000'
        ctx.beginPath()
        const hornX = enemyFacingRight ? enemy.x + 20 : enemy.x + 10
        ctx.moveTo(hornX, enemy.y + 2)
        ctx.lineTo(hornX + (enemyFacingRight ? 4 : -4), enemy.y - 4)
        ctx.lineTo(hornX + (enemyFacingRight ? 2 : -2), enemy.y + 4)
        ctx.fill()
        
        // Olho malvado (um só - perfil)
        ctx.fillStyle = '#FF0000'
        const enemyEyeX = enemyFacingRight ? enemy.x + 18 : enemy.x + 12
        ctx.fillRect(enemyEyeX, enemy.y + 5, 5, 5)
        ctx.fillStyle = '#000'
        ctx.fillRect(enemyEyeX + 2, enemy.y + 6, 2, 3)
        
        // Boca com dentes
        ctx.fillStyle = '#000'
        const mouthX = enemyFacingRight ? enemy.x + 16 : enemy.x + 8
        ctx.fillRect(mouthX, enemy.y + 12, 8, 4)
        ctx.fillStyle = '#FFF'
        ctx.fillRect(mouthX + 1, enemy.y + 12, 2, 2)
        ctx.fillRect(mouthX + 4, enemy.y + 12, 2, 2)
        
        // Braço da frente
        ctx.fillStyle = enemyColor
        const enemyArmX = enemyFacingRight ? enemy.x + 18 : enemy.x + 8
        ctx.fillRect(enemyArmX, enemy.y + 12, 4, 10)
        // Garra
        ctx.fillStyle = '#000'
        ctx.fillRect(enemyArmX, enemy.y + 20, 5, 4)
        
        // Pernas
        ctx.fillStyle = enemyColor
        ctx.fillRect(enemy.x + 10, enemy.y + enemy.height - 12, 4, 12)
        ctx.fillRect(enemy.x + 16, enemy.y + enemy.height - 12, 4, 12)

        // Check collision with player
        if (
          player.x < enemy.x + enemy.width &&
          player.x + player.width > enemy.x &&
          player.y < enemy.y + enemy.height &&
          player.y + player.height > enemy.y
        ) {
          if (powerUp === 'cpu') {
            // Com CPU power, destrói o inimigo
            enemiesToRemove.push(idx)
            setScore(prev => prev + 50)
          } else if (player.velocityY > 0 && player.y < enemy.y) {
            // Pula em cima do inimigo
            enemiesToRemove.push(idx)
            player.velocityY = JUMP_FORCE / 2
            setScore(prev => prev + 50)
          } else if (!invincible) {
            // Perdeu vida ao tocar em inimigo normal (apenas se não estiver invencível)
            setInvincible(true)
            setInvincibleTime(120) // 2 segundos de invencibilidade
            setLives(prev => {
              const newLives = prev - 1
              if (newLives <= 0) {
                setGameOver(true)
                saveGameScore(score)
              } else {
                // Reposiciona jogador sem reiniciar nível
                player.x = 100
                player.y = 400
                player.velocityY = 0
                player.velocityX = 0
              }
              return newLives
            })
          }
        }
      })
      
      // Remove inimigos marcados (de trás para frente para não afetar índices)
      for (let i = enemiesToRemove.length - 1; i >= 0; i--) {
        state.enemies.splice(enemiesToRemove[i], 1)
      }
      }


      // Update and draw items
      if (state.items && Array.isArray(state.items)) {
        state.items.forEach(item => {
          if (item.collected) return

          // Draw item
        if (item.type === 'coin') {
          ctx.fillStyle = '#FFD700'
          ctx.beginPath()
          ctx.arc(item.x + 12, item.y + 12, 12, 0, Math.PI * 2)
          ctx.fill()
          ctx.fillStyle = '#FFA500'
          ctx.beginPath()
          ctx.arc(item.x + 12, item.y + 12, 8, 0, Math.PI * 2)
          ctx.fill()
        } else if (item.type === 'cpu') {
          // Emoji de computador
          ctx.font = '28px Arial'
          ctx.fillText('💻', item.x - 2, item.y + 22)
        } else if (item.type === 'ram') {
          // Emoji de raio
          ctx.font = '28px Arial'
          ctx.fillText('⚡', item.x - 2, item.y + 22)
        }

        // Check collision with player
        if (
          player.x < item.x + item.width &&
          player.x + player.width > item.x &&
          player.y < item.y + item.height &&
          player.y + player.height > item.y
        ) {
          item.collected = true
          
          if (item.type === 'coin') {
            setScore(prev => prev + 10)
            gameStateRef.current.coins++
          } else if (item.type === 'cpu') {
            setPowerUp('cpu')
            setPowerUpTime(300)
            setScore(prev => prev + 25)
          } else if (item.type === 'ram') {
            setPowerUp('ram')
            setPowerUpTime(300)
            setScore(prev => prev + 25)
          }
        }
      })
      }

      // Update power-up timer
      if (powerUpTime > 0) {
        setPowerUpTime(prev => prev - 1)
        if (powerUpTime === 1) {
          setPowerUp(null)
        }
      }

      // Update invincibility timer
      if (invincibleTime > 0) {
        setInvincibleTime(prev => prev - 1)
        if (invincibleTime === 1) {
          setInvincible(false)
        }
      }

      // Check level completion - Só muda quando derrotar TODOS inimigos E coletar TODAS moedas
      if (!levelComplete && !state.boss && state.enemies && state.enemies.length === 0) {
        // Verifica se todas as moedas foram coletadas
        const allCoinsCollected = state.items && state.items.every(item => 
          item.type !== 'coin' || item.collected
        )
        
        if (allCoinsCollected) {
          console.log('LEVEL COMPLETE! All enemies defeated and all coins collected')
          setLevelComplete(true)
          setScore(prev => prev + 100) // Bônus por completar a fase
          setTimeout(() => {
            setLevel(prev => prev + 1)
            setTimeout(() => {
              setLevelComplete(false)
              initLevel()
            }, 100)
          }, 2000)
        }
      }

      // Draw player (San Code character) - PERFIL
      // Efeito de piscada quando invencível
      const shouldDraw = !invincible || Math.floor(Date.now() / 100) % 2 === 0
      
      if (shouldDraw) {
        const playerColor = powerUp === 'cpu' ? '#FFD700' : (powerUp === 'ram' ? '#00FF00' : '#0099FF')
        const facingRight = player.direction === 'right'
        
        // Corpo (retângulo principal)
        ctx.fillStyle = playerColor
        ctx.fillRect(player.x + 6, player.y + 10, player.width - 12, player.height - 18)
        
        // Cabeça (círculo)
        ctx.fillStyle = '#FFD4A3'
        ctx.beginPath()
        ctx.arc(player.x + player.width/2, player.y + 8, 8, 0, Math.PI * 2)
        ctx.fill()
        
        // Cabelo
        ctx.fillStyle = '#4A2C0F'
        ctx.beginPath()
        ctx.arc(player.x + player.width/2, player.y + 5, 8, Math.PI, Math.PI * 2)
        ctx.fill()
        
        // Olho (um só - perfil)
        ctx.fillStyle = '#FFF'
        const eyeX = facingRight ? player.x + 20 : player.x + 12
        ctx.beginPath()
      ctx.arc(eyeX, player.y + 8, 3, 0, Math.PI * 2)
      ctx.fill()
      
      // Pupila
      ctx.fillStyle = '#000'
      const pupilX = facingRight ? eyeX + 1 : eyeX - 1
      ctx.beginPath()
      ctx.arc(pupilX, player.y + 8, 1.5, 0, Math.PI * 2)
      ctx.fill()
      
      // Nariz (pequeno triângulo)
      ctx.fillStyle = '#FFB380'
      ctx.beginPath()
      const noseX = facingRight ? player.x + 24 : player.x + 8
      ctx.moveTo(noseX, player.y + 10)
      ctx.lineTo(noseX + (facingRight ? 3 : -3), player.y + 12)
      ctx.lineTo(noseX, player.y + 14)
      ctx.fill()
      
      // Boca (linha sorrindo)
      ctx.strokeStyle = '#000'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      const mouthX = facingRight ? player.x + 18 : player.x + 10
      ctx.moveTo(mouthX, player.y + 16)
      ctx.quadraticCurveTo(mouthX + (facingRight ? 4 : -4), player.y + 18, mouthX + (facingRight ? 6 : -6), player.y + 16)
      ctx.stroke()
      
      // Braço da frente
      ctx.fillStyle = '#FFD4A3'
      const armX = facingRight ? player.x + 18 : player.x + 8
      ctx.fillRect(armX, player.y + 14, 4, 12)
      
      // Mão
      ctx.fillRect(armX - 1, player.y + 24, 6, 4)
      
      // Braço de trás (mais escuro)
      ctx.fillStyle = '#FFB380'
      const backArmX = facingRight ? player.x + 8 : player.x + 18
      ctx.fillRect(backArmX, player.y + 14, 4, 10)
      
      // Pernas
      ctx.fillStyle = '#2C5AA0'
      const leg1X = facingRight ? player.x + 14 : player.x + 10
      const leg2X = facingRight ? player.x + 20 : player.x + 16
      ctx.fillRect(leg1X, player.y + player.height - 14, 5, 14)
      ctx.fillRect(leg2X, player.y + player.height - 14, 5, 14)
      
      // Pés (tênis)
      ctx.fillStyle = '#FFF'
      ctx.fillRect(leg1X - 2, player.y + player.height - 4, 8, 4)
      ctx.fillRect(leg2X - 2, player.y + player.height - 4, 8, 4)
      
      // Símbolo { } no peito
      ctx.fillStyle = '#FFF'
      ctx.font = 'bold 10px monospace'
      ctx.fillText('{ }', player.x + 10, player.y + 24)
      } // Fecha o if (shouldDraw)

      // Boss level indicator
      if (state.isBossLevel && state.boss) {
        ctx.fillStyle = '#FF0000'
        ctx.font = 'bold 24px Arial'
        ctx.fillText('⚠️ BOSS LEVEL ⚠️', CANVAS_WIDTH / 2 - 100, 30)
      }

      animationFrameId = requestAnimationFrame(gameLoop)
    }

    animationFrameId = requestAnimationFrame(gameLoop)

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOver, isPaused, score, powerUp, powerUpTime, level])

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'w', 'a', 's', 'd'].includes(e.key)) {
        e.preventDefault()
        if (!gameStarted) setGameStarted(true)
      }
      gameStateRef.current.keys[e.key] = true
    }

    const handleKeyUp = (e) => {
      gameStateRef.current.keys[e.key] = false
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  // Prevent scroll during game
  useEffect(() => {
    document.body.classList.add('game-active')
    return () => {
      document.body.classList.remove('game-active')
    }
  }, [])

  function handleRestart() {
    setScore(0)
    setLives(3)
    setLevel(1)
    setGameOver(false)
    setGameStarted(false)
    setPowerUp(null)
    setPowerUpTime(0)
    setScoreSaved(false)
    setTrialBlocked(false)
    setIsPaused(false)
    initLevel()
  }

  function handleTogglePause() {
    setIsPaused(!isPaused)
  }

  function handleToggleRanking() {
    if (!gameOver && !showRanking) {
      setIsPaused(true)
    }
    setShowRanking(!showRanking)
  }

  // Funções de controle touch mobile
  const handleJoystickStart = useCallback((e) => {
    if (gameOver || isPaused) return
    e.preventDefault()
    if (!gameStarted) setGameStarted(true)
    
    const touch = e.touches[0]
    const rect = joystickRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const deltaX = touch.clientX - centerX
    
    if (deltaX < -20) {
      gameStateRef.current.touchControls = { left: true, right: false, jump: gameStateRef.current.touchControls.jump }
      setTouchControls(prev => ({ ...prev, left: true, right: false }))
    } else if (deltaX > 20) {
      gameStateRef.current.touchControls = { left: false, right: true, jump: gameStateRef.current.touchControls.jump }
      setTouchControls(prev => ({ ...prev, left: false, right: true }))
    }
  }, [gameOver, isPaused, gameStarted])

  const handleJoystickMove = useCallback((e) => {
    if (gameOver || isPaused) return
    e.preventDefault()
    
    const touch = e.touches[0]
    const rect = joystickRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const deltaX = touch.clientX - centerX
    
    if (deltaX < -20) {
      gameStateRef.current.touchControls = { left: true, right: false, jump: gameStateRef.current.touchControls.jump }
      setTouchControls(prev => ({ ...prev, left: true, right: false }))
    } else if (deltaX > 20) {
      gameStateRef.current.touchControls = { left: false, right: true, jump: gameStateRef.current.touchControls.jump }
      setTouchControls(prev => ({ ...prev, left: false, right: true }))
    } else {
      gameStateRef.current.touchControls = { left: false, right: false, jump: gameStateRef.current.touchControls.jump }
      setTouchControls(prev => ({ ...prev, left: false, right: false }))
    }
  }, [gameOver, isPaused])

  const handleJoystickEnd = useCallback(() => {
    gameStateRef.current.touchControls = { left: false, right: false, jump: gameStateRef.current.touchControls.jump }
    setTouchControls(prev => ({ ...prev, left: false, right: false }))
  }, [])

  const handleJumpPress = useCallback(() => {
    if (gameOver || isPaused) return
    if (!gameStarted) setGameStarted(true)
    gameStateRef.current.touchControls = { ...gameStateRef.current.touchControls, jump: true }
    setTouchControls(prev => ({ ...prev, jump: true }))
  }, [gameOver, isPaused, gameStarted])

  const handleJumpRelease = useCallback(() => {
    gameStateRef.current.touchControls = { ...gameStateRef.current.touchControls, jump: false }
    setTouchControls(prev => ({ ...prev, jump: false }))
  }, [])

  return (
    <div className="sancode-container">
      <UserHeader user={user} gameName="SAN CODE" />

      <div className="game-layout">
        <GameStats 
          score={score} 
          lines={lives}
          level={level}
        />

        <div className="game-area">
          <canvas 
            ref={canvasRef} 
            width={CANVAS_WIDTH} 
            height={CANVAS_HEIGHT}
            className="game-canvas"
          />
          
          {/* Mobile Info Overlay */}
          <div className="mobile-info-overlay">
            <span>🏆 {score}</span>
            <span>❤️ {lives}</span>
            <span>📊 L{level}</span>
            {level % 10 === 0 && <span className="boss-indicator">👾 BOSS</span>}
          </div>

          {/* Power-up indicator */}
          {powerUp && (
            <div className="powerup-indicator">
              {powerUp === 'cpu' ? '💻 CPU POWER!' : '⚡ RAM SPEED!'}
              <div className="powerup-bar">
                <div 
                  className="powerup-fill" 
                  style={{ width: `${(powerUpTime / 300) * 100}%` }}
                />
              </div>
            </div>
          )}
          
          {levelComplete && (
            <div className="level-complete-overlay">
              <div className="level-complete-text">
                ✅ LEVEL {level} COMPLETE!
                <div className="bonus-points">+100 BONUS</div>
              </div>
            </div>
          )}
          
          {gameOver && (
            <div className="game-overlay">
              <div className="game-over-panel">
                {/* Personagem triste */}
                <div style={{
                  width: '80px',
                  height: '100px',
                  margin: '0 auto 20px',
                  position: 'relative'
                }}>
                  {/* Corpo */}
                  <div style={{
                    position: 'absolute',
                    left: '16px',
                    top: '32px',
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#0099FF',
                    borderRadius: '4px'
                  }}></div>
                  
                  {/* Cabeça */}
                  <div style={{
                    position: 'absolute',
                    left: '24px',
                    top: '8px',
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#FFD4A3',
                    borderRadius: '50%'
                  }}>
                    {/* Olhos tristes */}
                    <div style={{
                      position: 'absolute',
                      left: '6px',
                      top: '12px',
                      width: '6px',
                      height: '8px',
                      backgroundColor: '#000',
                      borderRadius: '50%'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      right: '6px',
                      top: '12px',
                      width: '6px',
                      height: '8px',
                      backgroundColor: '#000',
                      borderRadius: '50%'
                    }}></div>
                    
                    {/* Boca triste */}
                    <div style={{
                      position: 'absolute',
                      left: '8px',
                      bottom: '6px',
                      width: '16px',
                      height: '8px',
                      borderBottom: '2px solid #000',
                      borderRadius: '0 0 8px 8px',
                      transform: 'rotate(180deg)'
                    }}></div>
                  </div>
                  
                  {/* Braços caídos */}
                  <div style={{
                    position: 'absolute',
                    left: '8px',
                    top: '40px',
                    width: '8px',
                    height: '24px',
                    backgroundColor: '#FFD4A3',
                    borderRadius: '4px'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    right: '8px',
                    top: '40px',
                    width: '8px',
                    height: '24px',
                    backgroundColor: '#FFD4A3',
                    borderRadius: '4px'
                  }}></div>
                  
                  {/* Pernas */}
                  <div style={{
                    position: 'absolute',
                    left: '24px',
                    bottom: '4px',
                    width: '10px',
                    height: '20px',
                    backgroundColor: '#2C5AA0',
                    borderRadius: '2px'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    right: '24px',
                    bottom: '4px',
                    width: '10px',
                    height: '20px',
                    backgroundColor: '#2C5AA0',
                    borderRadius: '2px'
                  }}></div>
                </div>
                
                <h2>😢 GAME OVER</h2>
                <p className="final-score">Score: {score}</p>
                <p className="coins-collected">Coins: {gameStateRef.current.coins}</p>
                
                {savingScore && <p className="saving">💾 Salvando...</p>}
                {scoreSaved && <p className="saved">✅ Score Salvo!</p>}
                
                <div className="game-over-buttons">
                  <button onClick={handleRestart} className="play-button">
                    🔄 Jogar Novamente
                  </button>
                  <button onClick={handleToggleRanking} className="ranking-button">
                    🏆 Ver Ranking
                  </button>
                </div>
              </div>
            </div>
          )}

          {isPaused && !gameOver && !showRanking && (
            <div className="game-overlay">
              <div className="pause-panel">
                <h2>⏸️ PAUSADO</h2>
                <p>Pressione ESPAÇO para continuar</p>
                <button onClick={handleTogglePause} className="resume-button">
                  ▶️ Continuar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="game-controls">
          {!gameStarted && (
            <>
              <div className="controls-section">
                <h3>🎮 Controles</h3>
                <div className="controls-grid">
                  <div className="control-item">
                    <kbd>←</kbd><kbd>→</kbd>
                    <span>Mover</span>
                  </div>
                  <div className="control-item">
                    <kbd>↑</kbd> ou <kbd>ESPAÇO</kbd>
                    <span>Pular</span>
                  </div>
                  <div className="control-item">
                    <kbd>P</kbd>
                    <span>Pausar</span>
                  </div>
                </div>
              </div>

              <div className="controls-section">
                <h3>💎 Power-ups</h3>
                <div className="powerups-info">
                  <div className="powerup-item">
                    <span className="powerup-icon">💻</span>
                    <div>
                      <strong>CPU</strong>
                      <p>Destrói inimigos ao tocar</p>
                    </div>
                  </div>
                  <div className="powerup-item">
                    <span className="powerup-icon">⚡</span>
                    <div>
                      <strong>RAM</strong>
                      <p>Aumenta velocidade</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="button-group">
            <button 
              onClick={handleTogglePause}
              className="control-btn"
              disabled={gameOver}
            >
              {isPaused ? '▶️ Continuar' : '⏸️ Pausar'}
            </button>
            <button 
              onClick={handleToggleRanking}
              className="control-btn ranking-btn"
            >
              🏆 Ranking
            </button>
            {gameOver && (
              <button onClick={handleRestart} className="control-btn restart-btn">
                🔄 Reiniciar
              </button>
            )}
          </div>
        </div>

        {/* Ranking - Sempre visível na sidebar */}
        <div className="ranking-section">
          <GameRanking 
            gameName="sancode"
            currentScore={score}
            user={user}
          />
        </div>
      </div>

      {trialBlocked && (
        <div className="trial-blocked-overlay">
          <div className="trial-blocked-modal">
            <div className="trial-blocked-header">
              <h2>🎮 Versão Trial Concluída!</h2>
              <p>Você completou os 3 níveis de demonstração</p>
            </div>
            
            <div className="trial-blocked-content">
              <div className="trial-info">
                <h3>🕹️ Continue Jogando!</h3>
                <p>Desbloqueie o jogo completo San Code:</p>
                <div className="price-options">
                  <div className="price-tag">
                    <span className="price">R$ 7,99</span>
                    <span className="game-type">Apenas San Code</span>
                  </div>
                  <div className="or-divider">ou</div>
                  <div className="price-tag best-offer">
                    <span className="price">R$ 40,00</span>
                    <span className="game-type">TODOS os Jogos</span>
                  </div>
                </div>
              </div>

              <div className="trial-benefits">
                <h4>✨ Com o jogo completo você terá:</h4>
                <ul>
                  <li>✅ Níveis ilimitados</li>
                  <li>✅ Todos os power-ups</li>
                  <li>✅ Ranking global</li>
                  <li>✅ Sem restrições</li>
                  <li>✅ Acesso vitalício</li>
                </ul>
              </div>

              <div className="trial-buttons">
                <button 
                  className="btn-unlock"
                  onClick={() => setShowPayment(true)}
                >
                  💳 Desbloquear Agora
                </button>
                <button 
                  className="btn-back"
                  onClick={handleRestart}
                >
                  🔄 Jogar Novamente (Trial)
                </button>
              </div>

              <p className="trial-note">
                * Pagamento único - Acesso completo ao jogo
              </p>
            </div>
          </div>
        </div>
      )}

      {showRanking && (
        <RankingModal
          globalRanking={globalRanking}
          personalRanking={personalRanking}
          onClose={() => {
            setShowRanking(false)
            if (!gameOver) setIsPaused(false)
          }}
        />
      )}

      <PaymentModal
        show={showPayment}
        onClose={() => setShowPayment(false)}
        onPaymentComplete={(paymentData) => {
          console.log('Payment complete:', paymentData)
          setShowPayment(false)
          // Recarrega o jogo para desbloquear
          if (onBackToMenu) {
            onBackToMenu()
          }
        }}
        gameToUnlock="sancode"
        userProgress={userProgress}
        user={user}
      />

      {/* Controles Mobile Touch - apenas quando o jogo começou */}
      {gameStarted && !gameOver && !isPaused && !showRanking && (
        <div className="mobile-controls">
          {/* Joystick Virtual - Esquerda */}
          <div 
            ref={joystickRef}
            className="virtual-joystick"
            onTouchStart={handleJoystickStart}
            onTouchMove={handleJoystickMove}
            onTouchEnd={handleJoystickEnd}
          >
            <div className="joystick-base">
              <div className={`joystick-stick ${touchControls.left ? 'left' : touchControls.right ? 'right' : ''}`}>
                <span className="joystick-arrows">
                  <span className={touchControls.left ? 'active' : ''}>←</span>
                  <span className={touchControls.right ? 'active' : ''}>→</span>
                </span>
              </div>
            </div>
            <div className="joystick-label">MOVER</div>
          </div>

          {/* Botão de Pulo - Direita */}
          <div 
            ref={jumpButtonRef}
            className={`jump-button ${touchControls.jump ? 'pressed' : ''}`}
            onTouchStart={handleJumpPress}
            onTouchEnd={handleJumpRelease}
          >
            <span className="jump-icon">↑</span>
            <div className="jump-label">PULAR</div>
          </div>
        </div>
      )}
    </div>
  )
}
